import { useState, useEffect, useRef, useCallback } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/acquireAgent.css'

// ── Utilities ─────────────────────────────────────────────────────────────────
const sleep   = (ms) => new Promise((r) => setTimeout(r, ms))
const money   = (v)  => `$${Math.round(Number(v) || 0).toLocaleString()}`
const today   = ()   => new Date().toISOString().split('T')[0]
const addDays = (d, n) => {
  const dt = new Date((d || today()) + 'T12:00:00')
  dt.setDate(dt.getDate() + n)
  return dt.toISOString().split('T')[0]
}
const fmtDate = (d) =>
  d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

// ── Pipeline factory ──────────────────────────────────────────────────────────
function buildPipeline(ds) {
  const raw      = ds.rawDealData || {}
  const price    = Number(raw.price) || 2_850_000
  const units    = Number(raw.property?.units) || 24
  const city     = raw.location?.city  || 'Austin'
  const st       = raw.location?.state || 'TX'
  const addr     = raw.location?.address || '4820 Oak Creek Blvd'
  const noi      = Number(ds.extractedFinancials?.noi) || Math.round(price * 0.062)
  const capRate  = price > 0 ? (noi / price) * 100 : 6.2
  const loanAmt  = Math.round(price * 0.725)
  const downPmt  = price - loanAmt
  const rate     = 6.875
  const revenue  = Math.round(noi / 0.38)
  const expenses = revenue - noi
  const dscr     = 1.24
  const closeDate  = addDays(today(), 42)
  const ddDeadline = addDays(today(), 21)
  const earnestDue = addDays(today(), 5)
  const exitValue  = Math.round((noi * 1.15) / ((capRate + 0.25) / 100))
  const escrowNum  = `ESC-${Math.floor(Math.random() * 90000 + 10000)}`
  const hasUserDeal = Boolean(ds.dealId && raw.price)
  const hasApprovedLender = Boolean(ds.lenderMatches?.topRecommendation?.userApproved)

  const selectedLender = ds.lenderMatches?.topRecommendation?.lenderName || 'Your Selected Lender'
  const lenderEmailToken = `[${selectedLender} - contact email]`

  const escrowCompany = ds.escrowDetails?.escrowCompany || 'Your Escrow / Title Company'
  const escrowOfficer = ds.escrowDetails?.escrowOfficer || 'Your Escrow Officer'
  const escrowEmail   = ds.escrowDetails?.escrowOfficer
    ? `[${ds.escrowDetails.escrowOfficer} - email]`
    : '[escrow officer email]'
  const pmCompany = ds.assetManagement?.managementPlan?.pmCompany || 'Your Property Manager'

  const pipeline = [
    // ── STEP 0: Quick Deal Analysis — Upload OM ──────────────────────────────
    {
      id: 'qda_om_upload', label: 'Upload OM for Quick Deal Analysis', module: 'quick-deal', icon: 'Q',
      permission: 'qda_om',
      permissionDetail: {
        title: 'Upload Offering Memorandum',
        description: 'Upload your Offering Memorandum (OM) in Quick Deal Analysis so the AI can extract key deal metrics instantly.',
      },
      run: () => {},
    },

    // ── STEP 1: Quick Deal Analysis — Run analysis ───────────────────────────
    {
      id: 'qda_analysis', label: 'Running Quick Deal Analysis', module: 'quick-deal', icon: 'Q',
      run: ({ update }) => update({ quickDealAnalyzed: true }),
    },

    // ── STEP 2: Pipeline Confirmation ────────────────────────────────────────
    {
      id: 'pipeline_confirm', label: 'Continue into Acquisition Pipeline?', module: 'quick-deal', icon: 'P',
      permission: 'pipeline_confirm',
      permissionDetail: {
        title: 'Continue into Acquisition Pipeline',
        description: 'Quick Deal Analysis complete! Ready to enter the full Acquisition Pipeline starting with Deal Lead Generation.',
      },
      run: ({ update }) => update({ pipelineConfirmed: true }),
    },

    // ── Module 1: Deal Lead Generation ──────────────────────────────────────
    hasUserDeal ? {
      id: 'use_selected_deal', label: 'Using confirmed deal selection', module: 1, icon: 'D',
      run: ({ update }) => update({ pipelineStage: 1, pipelineStatus: 'in_progress' }),
    } : {
      id: 'select_deal_required', label: 'Deal selection required', module: 1, icon: 'D',
      permission: 'decision',
      permissionDetail: {
        title: 'Select a Deal in Deal Lead Generation',
        moduleNumber: 1,
        action: 'Open Module 1, select a deal card, then click Confirm Deal Selection at the top of the deals list.',
        reason: 'AcquireOS can rank and score deals, but the investment choice must remain yours.',
      },
      run: () => {},
    },

    // ── Module 2: Market Intelligence ───────────────────────────────────────
    {
      id: 'market_analysis', label: 'Running market intelligence', module: 2, icon: 'M',
      run: ({ update }) => update({
        marketAnalysis: {
          marketScore: 78, city, state: st,
          population: 978_908, populationGrowth: 2.8, jobGrowth: 3.1,
          vacancyRate: 5.2, rentGrowth: 4.2,
          verdict: 'Strong growth market with favorable supply-demand dynamics',
          signals: ['Population Growth', 'Job Market Strength', 'Rent Growth Trend'],
        },
      }),
    },

    // ── Module 3: Deal Intake — Upload Documents ─────────────────────────────
    {
      id: 'doc_upload_required', label: 'Upload deal documents in Deal Intake', module: 3, icon: 'D',
      permission: 'doc_upload',
      permissionDetail: {
        title: 'Upload Deal Documents',
        description: 'Upload all deal documents (rent roll, T12 operating statements, inspection reports, leases) in Deal Intake & Documents so the AI can validate the financials.',
      },
      run: ({ update }) => update({ documentsUploaded: true }),
    },

    // ── Module 3: Deal Intake — Extract Financials ───────────────────────────
    {
      id: 'extract_financials', label: 'Extracting and validating financials', module: 3, icon: 'F',
      run: ({ update }) => {
        const latest = useDealStore.getState().dealState
        if (!latest.financialDocuments?.omUploaded) {
          return { blocked: true, reason: 'OM not uploaded' }
        }
        update({
          extractedFinancials: {
            noi,
            revenue: {
              totalRevenue: revenue,
              rentRoll: { totalUnits: units, occupancyRate: 93, avgRent: Math.round(revenue / 12 / units) },
            },
            expenses: { totalExpenses: expenses },
            dataQuality: { score: 87, missingFields: [], confidence: 'High' },
            financialModeling: {
              cashFlow: { annualCashFlow: Math.round((noi - loanAmt * 0.073) * 0.95) },
            },
          },
          financialModeling: {
            cashFlow: { annualCashFlow: Math.round((noi - loanAmt * 0.073) * 0.95) },
          },
        })
      },
    },

    // Module 4: Underwriting
    {
      id: 'underwrite', label: 'Running full underwriting model', module: 4, icon: 'U',
      run: ({ update }) => update({
        underwritingMetrics: {
          capRate: { entry: capRate, exit: capRate + 0.25 },
          dscr: { current: dscr },
          debtService: {
            loanAmount: loanAmt, ltv: 0.725, interestRate: rate,
            annual: Math.round(loanAmt * 0.073),
            monthly: Math.round(loanAmt * 0.073 / 12),
          },
          cashOnCash: 7.8, irr: 14.2, equityMultiple: 1.82, noi,
        },
      }),
    },

    // Module 5: Deal Score
    {
      id: 'score_deal', label: 'Scoring deal and generating offer', module: 5, icon: 'S',
      run: ({ update }) => update({
        scoreAndOffer: {
          numericalScore: 74, verdict: 'Strong Buy',
          offerPrice: Math.round(price * 0.97),
          maxPrice: Math.round(price * 1.02),
          confidence: 'High',
          keyStrengths: ['Strong NOI', 'Growing market', 'Below-market rents'],
          keyRisks: ['DSCR near floor', 'Older vintage'],
        },
      }),
    },

    // Module 6: Lender Match
    {
      id: 'match_lenders', label: 'Matching lenders to deal profile', module: 6, icon: 'L',
      run: ({ update }) => update({
        lenderMatches: {
          matchedLenders: [
            { lenderName: 'Arbor Realty Trust', rate: 6.875, ltv: 72.5, confidence: 82, product: 'Agency' },
            { lenderName: 'Walker & Dunlop',    rate: 6.625, ltv: 70.0, confidence: 76, product: 'Fannie Mae' },
            { lenderName: 'Berkadia',           rate: 7.125, ltv: 75.0, confidence: 71, product: 'Bridge' },
          ],
          topRecommendation: null,
        },
      }),
    },

    hasApprovedLender ? {
      id: 'confirmed_lender', label: 'Using approved lender selection', module: 6, icon: 'L',
      run: () => {},
    } : {
      id: 'select_lender_required', label: 'Lender approval required', module: 6, icon: 'L',
      permission: 'decision',
      permissionDetail: {
        title: 'Select or approve a lender in Module 6',
        moduleNumber: 6,
        action: 'Open Module 6, choose a lender from the ranked list, then approve the selected lender to advance.',
        reason: 'AcquireOS can rank financing options, but the lender decision must be explicitly approved by you.',
      },
      run: () => {},
    },

    // ── Syndication Decision ─────────────────────────────────────────────────
    {
      id: 'syndicate_decision', label: 'Deal Syndication — your decision', module: 7, icon: 'S',
      permission: 'syndicate',
      permissionDetail: {
        title: 'Deal Syndication Engine',
        description: 'Do you need to syndicate this deal with co-investors to bridge the equity gap?',
      },
      run: () => {},
    },

    // ── Module 7: Deal Syndication Engine (runs only if user said YES) ───────
    {
      id: 'run_syndication', label: 'Running Deal Syndication Engine', module: 7, icon: 'S',
      run: ({ update }) => update({
        syndicateData: {
          syndicationEnabled: true,
          completedAt: new Date().toISOString(),
        },
      }),
    },

    // ── Module 8: Loan Package ───────────────────────────────────────────────
    {
      id: 'generate_loan_package', label: 'Generating loan package documents', module: 8, icon: 'P',
      run: ({ update }) => update({
        loanPackage: {
          documentId: `pkg_${Date.now()}`,
          executiveSummary: `${units}-unit multifamily at ${addr}. Requesting ${money(loanAmt)} at ${rate}% (72.5% LTV). NOI: ${money(noi)}. DSCR: ${dscr}x. Cap rate: ${capRate.toFixed(2)}%.`,
          status: 'ready', completeness: 91,
          lenderName: selectedLender, loanAmount: loanAmt, ltv: 72.5, rate,
          sections: ['executive_summary', 't12_income_statement', 'rent_roll', 'property_photos', 'site_inspection'],
          generatedAt: new Date().toISOString(),
        },
      }),
    },

    // Module 8: Outreach
    {
      id: 'generate_outreach', label: 'Generating outreach communications', module: 8, icon: 'O',
      run: ({ update }) => update({
        outreachMaterials: {
          emailTemplates: [
            {
              id: 'lender_intro', channel: 'email', recipientType: 'lender',
              subject: `Loan Request: ${addr} - ${money(loanAmt)}`,
              message: `Dear ${selectedLender} Team,\n\nPlease find our loan request for ${addr}, ${city} ${st}. We are seeking ${money(loanAmt)} at 72.5% LTV. NOI: ${money(noi)}. DSCR: ${dscr}x.\n\nFull loan package attached.\n\n- AcquireOS AI Acquisitions LLC`,
              generatedAt: new Date().toISOString(),
            },
            {
              id: 'broker_intro', channel: 'email', recipientType: 'broker',
              subject: `Interest in ${addr}`,
              message: `Hi,\n\nWe are actively pursuing ${addr}. Our offer will be at ${money(Math.round(price * 0.97))} with a 60-day DD period.\n\n- AcquireOS AI Acquisitions LLC`,
              generatedAt: new Date().toISOString(),
            },
          ],
          followUpSequenceCreated: true,
        },
      }),
    },

    // Module 9: Actions & Follow-Up
    {
      id: 'create_action_plan', label: 'Creating action plan and timeline', module: 9, icon: 'A',
      run: ({ update }) => update({
        actionPlan: {
          tasks: [
            { id: 'submit_lender_pkg', title: `Submit loan package to ${selectedLender}`,    status: 'ready', priority: 'urgent', category: 'lender' },
            { id: 'send_loi',          title: 'Send executed LOI to seller broker',           status: 'ready', priority: 'urgent', category: 'legal' },
            { id: 'engage_attorney',   title: 'Engage real estate attorney for PSA review',   status: 'ready', priority: 'high',   category: 'legal' },
            { id: 'order_inspection',  title: 'Order property inspection',                    status: 'ready', priority: 'high',   category: 'dd' },
            { id: 'open_escrow',       title: `Open escrow with ${escrowCompany}`,            status: 'ready', priority: 'high',   category: 'closing' },
            { id: 'wire_earnest',      title: `Wire earnest money ${money(Math.round(price * 0.01))}`, status: 'ready', priority: 'urgent', category: 'closing' },
          ],
          timeline: `LOI by ${fmtDate(addDays(today(), 7))} - PSA by ${fmtDate(addDays(today(), 14))} - Close ${fmtDate(closeDate)}`,
          priorities: ['Submit loan package', 'Send LOI', 'Engage attorney', 'Order inspection'],
          createdAt: new Date().toISOString(),
        },
      }),
    },

    // PERMISSION: Outreach email
    {
      id: 'email_lender_outreach', label: `Send loan package to ${selectedLender}`, module: 9, icon: 'E',
      permission: 'email',
      permissionDetail: {
        to: lenderEmailToken,
        subject: `Loan Request: ${addr} - ${money(loanAmt)}`,
        preview: `Dear ${selectedLender} Team, please find our loan request for ${addr}, ${city} ${st}. Requesting ${money(loanAmt)} at 72.5% LTV. NOI: ${money(noi)}. DSCR: ${dscr}x. Full loan package attached. - AcquireOS AI Acquisitions LLC`,
      },
      run: ({ update }) => update({
        agentActions: [{ type: 'lender_email_sent', to: lenderEmailToken, at: new Date().toISOString() }],
      }),
    },

    // Module 10: LOI Generator
    {
      id: 'draft_loi', label: 'Drafting Letter of Intent', module: 10, icon: 'L',
      run: ({ update }) => update({
        loiDocument: {
          documentId: `loi_${Date.now()}`, status: 'draft',
          offerPrice: Math.round(price * 0.97),
          earnestMoney: { amount: Math.round(price * 0.01), percentage: 1 },
          timeline: { dueDiligenceDays: 60, closingDays: 90 },
          financingContingency: true, assignmentAllowed: true, sellerCredits: 0,
          generatedAt: new Date().toISOString(),
        },
      }),
    },

    // Module 11: PSA Assistant
    {
      id: 'draft_psa', label: 'Generating PSA and clause checklist', module: 11, icon: 'P',
      run: ({ update }) => update({
        psaDocument: {
          documentId: `psa_${Date.now()}`, status: 'draft',
          readinessScore: 84, attorneyReviewRequired: true,
          clauses: ['purchase-price','earnest-money','due-diligence','financing',
                    'title-survey','representations','closing-conditions',
                    'default-remedies','assignment','confidentiality'],
          terms: {
            purchasePrice: Math.round(price * 0.97),
            earnestMoneyAmount: Math.round(price * 0.01),
            dueDiligenceDays: 60, closingDays: 90, financingContingency: true,
          },
          generatedAt: new Date().toISOString(),
        },
      }),
    },

    // Module 12: Escrow & Title
    {
      id: 'setup_escrow', label: 'Opening escrow and setting timeline', module: 12, icon: 'E',
      run: ({ update }) => update({
        escrowDetails: {
          escrowNumber: escrowNum,
          escrowCompany, escrowOfficer,
          closeDate, ddDeadline, earnestDue, status: 'open',
          openedAt: new Date().toISOString(),
        },
        closingDetails: {
          closingDate: closeDate, cdDate: addDays(today(), 35),
          lenderName: selectedLender,
          wireAmt: String(downPmt + Math.round(price * 0.025)),
          completedSteps: [],
        },
      }),
    },

    // PERMISSION: DD status email
    {
      id: 'email_dd_status', label: 'Send DD status email to escrow officer', module: 12, icon: 'E',
      permission: 'email',
      permissionDetail: {
        to: escrowEmail,
        subject: `Due Diligence Open - ${addr}, ${city} ${st}`,
        preview: `Hi ${escrowOfficer}, please confirm our DD period is open through ${fmtDate(ddDeadline)}. Closing target remains ${fmtDate(closeDate)}. We will send inspection contacts within 48 hours. - AcquireOS AI Acquisitions LLC`,
      },
      run: ({ update }) => update({
        agentActions: [{ type: 'email_sent', to: escrowEmail, at: new Date().toISOString() }],
      }),
    },

    // Module 13: Due Diligence
    {
      id: 'run_due_diligence', label: 'Running due diligence checklist', module: 13, icon: 'D',
      run: ({ update }) => update({
        dueDiligence: {
          checkedItems: ['zoning','licenses','insurance','bank_stmts','tax_returns','permit_pull','appraisal','pest'],
          riskScoring: { overallRisk: 'Medium', score: 61 },
          finalClearanceConfirmed: true,
          savedAt: new Date().toISOString(),
        },
        pipelineStage: 13,
      }),
    },

    // Module 14: Closing
    {
      id: 'closing_costs', label: 'Calculating itemized closing costs', module: 14, icon: 'C',
      run: ({ update }) => update({
        closingDetails: {
          closingDate: closeDate, cdDate: addDays(today(), 35),
          lenderName: selectedLender,
          itemizedCosts: Math.round(price * 0.0248),
          cashToClose: downPmt + Math.round(price * 0.0248),
          completedSteps: ['loan_commitment','cd_received','cd_approved'],
          recording: { deedRegistered: true },
        },
        pipelineStage: 14,
      }),
    },

    // PERMISSION: Wire transfer
    {
      id: 'wire_auth', label: 'Authorize closing wire transfer', module: 14, icon: 'W',
      permission: 'wire',
      permissionDetail: {
        amount: downPmt + Math.round(price * 0.0248),
        to: `${escrowCompany} - Escrow Trust Account`,
        bank: 'Wells Fargo Bank - Routing: 121000248 - Acct: xxxx7843',
        memo: `Escrow closing funds - ${addr}`,
        warning: 'Verify routing and account by phone before approving.',
      },
      run: ({ update }) => update({
        agentActions: [{ type: 'wire_authorized', amount: downPmt + Math.round(price * 0.0248), at: new Date().toISOString() }],
      }),
    },

    // Module 15: Asset Management
    {
      id: 'asset_plan', label: 'Setting up asset management plan', module: 15, icon: 'A',
      run: ({ update }) => update({
        assetManagement: {
          kpiDashboard: {
            occupancy: 93, rent: Math.round(revenue / 12 / units),
            noi, capRate, dscr, activatedAt: new Date().toISOString(),
          },
          managementPlan: { pmCompany, pmFee: '8', pmSoftware: 'Yardi', type: 'third_party' },
          holdStrategy: {
            years: '5', exitCapRate: (capRate + 0.25).toFixed(2),
            strategy: 'sell', projectedValue: exitValue,
          },
        },
        pipelineStage: 15,
      }),
    },

    // Module 16: Exit Strategy
    {
      id: 'exit_analysis', label: 'Modeling exit scenarios', module: 16, icon: 'X',
      run: ({ update }) => update({
        exitStrategy: {
          recommendation: 'sell', scenario: 'base',
          exitTimeline: '5', exitYear: '5',
          exitCapRate: (capRate + 0.25).toFixed(2),
          noiGrowth: '3', sellingCostPct: '4',
          completedAt: new Date().toISOString(),
          analysis: { sell: { irr: 14.2, equityMultiple: 1.82, projectedValue: exitValue } },
        },
        pipelineStage: 16,
      }),
    },

    // PERMISSION: Investor report
    {
      id: 'investor_report', label: 'Send investor exit report', module: 16, icon: 'R',
      permission: 'email',
      permissionDetail: {
        to: 'investors@acquireos.ai',
        subject: `Acquisition Complete - ${addr}, ${city} ${st}`,
        preview: `All 16 acquisition modules completed. 5-year projected IRR: 14.2%. Exit value: ${money(exitValue)}. Equity multiple: 1.82x. Recommended strategy: Sell at Year 5. Full underwriting package attached.`,
      },
      run: ({ update }) => update({
        agentActions: [{ type: 'report_sent', at: new Date().toISOString() }],
      }),
    },
  ]

  return pipeline
}

// ── Resume index ──────────────────────────────────────────────────────────────
function findResumeIndex(pipeline, ds) {
  const agentActions = ds.agentActions || []
  const isDone = {
    qda_om_upload:          () => !!ds.quickDealAnalyzed || !!ds.financialDocuments?.omUploaded,
    qda_analysis:           () => !!ds.quickDealAnalyzed,
    pipeline_confirm:       () => !!ds.pipelineConfirmed,
    use_selected_deal:      () => !!ds.dealId,
    select_deal_required:   () => !!ds.dealId,
    market_analysis:        () => !!ds.marketAnalysis?.marketScore,
    doc_upload_required:    () => !!ds.documentsUploaded || !!(ds.financialDocuments?.omUploaded),
    extract_financials:     () => !!(ds.extractedFinancials?.noi),
    underwrite:             () => !!(ds.underwritingMetrics?.capRate),
    score_deal:             () => !!(ds.scoreAndOffer?.numericalScore),
    match_lenders:          () => !!(ds.lenderMatches?.matchedLenders?.length),
    confirmed_lender:       () => !!(ds.lenderMatches?.topRecommendation?.userApproved),
    select_lender_required: () => !!(ds.lenderMatches?.topRecommendation?.userApproved),
    syndicate_decision:     () => !!ds.syndicateDecisionMade,
    run_syndication:        () => !!ds.syndicateData?.completedAt || !!ds.syndicateSkipped,
    generate_loan_package:  () => !!ds.loanPackage?.documentId,
    generate_outreach:      () => !!(ds.outreachMaterials?.emailTemplates?.length),
    create_action_plan:     () => !!(ds.actionPlan?.tasks?.length),
    email_lender_outreach:  () => agentActions.some(a => a.type === 'lender_email_sent'),
    draft_loi:              () => !!ds.loiDocument?.documentId,
    draft_psa:              () => !!ds.psaDocument?.documentId,
    setup_escrow:           () => !!ds.escrowDetails?.escrowNumber,
    email_dd_status:        () => agentActions.some(a => a.type === 'email_sent'),
    run_due_diligence:      () => !!(ds.dueDiligence?.finalClearanceConfirmed),
    closing_costs:          () => !!(ds.closingDetails?.itemizedCosts),
    wire_auth:              () => agentActions.some(a => a.type === 'wire_authorized'),
    asset_plan:             () => !!(ds.assetManagement?.kpiDashboard),
    exit_analysis:          () => !!(ds.exitStrategy?.completedAt),
    investor_report:        () => agentActions.some(a => a.type === 'report_sent'),
  }

  for (let i = 0; i < pipeline.length; i++) {
    const check = isDone[pipeline[i].id]
    if (!check || !check()) return i
  }
  return pipeline.length
}

// ── Main Agent Component ──────────────────────────────────────────────────────
export default function AcquireAgent() {
  const { dealState, updateDealState, forceNavigateToModule } = useDealStore()
  const lenderApprovalReady = !!dealState.lenderMatches?.topRecommendation?.userApproved

  const [status,             setStatus]           = useState('idle')
  const [controlMenuOpen,    setControlMenuOpen]   = useState(false)
  const [permModal,          setPermModal]         = useState(null)
  const [permMinimized,      setPermMinimized]     = useState(false)
  const [omModalOpen,        setOmModalOpen]       = useState(false)
  const [omMinimized,        setOmMinimized]       = useState(false)
  const [dealModalOpen,      setDealModalOpen]     = useState(false)
  const [dealMinimized,      setDealMinimized]     = useState(false)
  const [qdaModalOpen,       setQdaModalOpen]      = useState(false)
  const [pipelineModalOpen,  setPipelineModalOpen] = useState(false)
  const [docModalOpen,       setDocModalOpen]      = useState(false)
  const [syndicateModalOpen, setSyndicateModalOpen]= useState(false)
  const [dealError,          setDealError]         = useState('')
  const [omError,            setOmError]           = useState('')
  const [docError,           setDocError]          = useState('')
  const [stepIdx,            setStepIdx]           = useState(0)
  const [currentStep,        setCurrentStep]       = useState('')

  const runningRef   = useRef(false)
  const stepRef      = useRef(0)
  const blockedAtRef = useRef(0)

  useEffect(() => {
    const savedStep   = localStorage.getItem('aq_agent_blocked_step')
    const savedReason = localStorage.getItem('aq_agent_blocked_reason') || ''
    const savedIdx    = parseInt(localStorage.getItem('aq_agent_step_idx') || '0')
    if (savedStep !== null) {
      blockedAtRef.current = parseInt(savedStep)
      stepRef.current      = savedIdx
      setStepIdx(savedIdx)
      setStatus('blocked')
      if (savedReason === 'OM not uploaded') {
        setOmModalOpen(true)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!controlMenuOpen) return
    const handler = (e) => {
      if (!e.target.closest('.aq-control-menu') && !e.target.closest('.aq-agent-fab')) {
        setControlMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [controlMenuOpen])

  const clearBlockedState = () => {
    localStorage.removeItem('aq_agent_blocked_step')
    localStorage.removeItem('aq_agent_blocked_reason')
    localStorage.removeItem('aq_agent_step_idx')
  }

  const runFrom = useCallback(async (fromIdx) => {
    if (runningRef.current) return
    runningRef.current = true
    setStatus('running')

    const pipeline = buildPipeline(useDealStore.getState().dealState)
    let lastModule = null

    for (let i = fromIdx; i < pipeline.length; i++) {
      if (!runningRef.current) break
      stepRef.current = i
      setStepIdx(i)
      setCurrentStep(pipeline[i].label)

      const step = pipeline[i]

      // Navigate to the step's module whenever it changes
      if (step.module && step.module !== lastModule) {
        forceNavigateToModule(step.module)
        lastModule = step.module
        await sleep(400)
      }

      await sleep(700 + Math.random() * 600)

      if (step.permission) {
        const permId = Date.now() + Math.random()
        stepRef._permId   = permId
        stepRef._permIdx  = i
        stepRef._permStep = step

        if (step.id === 'qda_om_upload') {
          forceNavigateToModule('quick-deal')
          await sleep(500)
          setQdaModalOpen(true)
        } else if (step.id === 'pipeline_confirm') {
          setPipelineModalOpen(true)
        } else if (step.id === 'select_deal_required') {
          forceNavigateToModule(1)
          await sleep(500)
          setDealModalOpen(true)
        } else if (step.id === 'doc_upload_required') {
          forceNavigateToModule(3)
          await sleep(500)
          setDocModalOpen(true)
        } else if (step.id === 'select_lender_required') {
          forceNavigateToModule(6)
          await sleep(500)
          setPermModal({ step, permId, permIdx: i })
        } else if (step.id === 'syndicate_decision') {
          forceNavigateToModule(7)
          await sleep(500)
          setSyndicateModalOpen(true)
        } else {
          setPermModal({ step, permId, permIdx: i })
        }

        setStatus('waiting')
        runningRef.current = false
        return
      }

      const result = step.run({ update: updateDealState })

      if (result && result.blocked) {
        blockedAtRef.current = i
        localStorage.setItem('aq_agent_blocked_step',   String(i))
        localStorage.setItem('aq_agent_blocked_reason', result.reason || '')
        localStorage.setItem('aq_agent_step_idx',       String(i))

        if (result.reason === 'OM not uploaded') {
          forceNavigateToModule(3)
          await sleep(500)
          setOmModalOpen(true)
        }
        setStatus('blocked')
        runningRef.current = false
        return
      }

      await sleep(280)
    }

    if (runningRef.current) {
      setStatus('done')
      setCurrentStep('All 16 modules complete!')
      forceNavigateToModule(16)
    }
    runningRef.current = false
  }, [forceNavigateToModule, updateDealState])

  const handleStart = useCallback(() => {
    setControlMenuOpen(false)
    const currentState = useDealStore.getState().dealState
    const pipeline     = buildPipeline(currentState)
    const resumeFrom   = findResumeIndex(pipeline, currentState)

    if (resumeFrom >= pipeline.length) {
      setStatus('done')
      setCurrentStep('All modules already complete.')
      forceNavigateToModule(16)
      return
    }

    forceNavigateToModule('quick-deal')
    runFrom(resumeFrom)
  }, [forceNavigateToModule, runFrom])

  // QDA OM uploaded — continue agent
  const handleQdaOmUploaded = useCallback(async () => {
    setQdaModalOpen(false)
    const { _permStep, _permIdx } = stepRef
    _permStep?.run({ update: updateDealState })
    updateDealState({ quickDealAnalyzed: true })
    await sleep(300)
    runFrom((_permIdx ?? 0) + 1)
  }, [runFrom, updateDealState])

  // Pipeline confirmation — continue into acquisition pipeline
  const handlePipelineConfirmed = useCallback(async () => {
    setPipelineModalOpen(false)
    const { _permStep, _permIdx } = stepRef
    _permStep?.run({ update: updateDealState })
    await sleep(300)
    runFrom((_permIdx ?? 0) + 1)
  }, [runFrom, updateDealState])

  // Doc upload confirmed — continue
  const handleDocUploaded = useCallback(() => {
    const latestState = useDealStore.getState().dealState
    if (!latestState.financialDocuments?.omUploaded && !latestState.documentsUploaded) {
      setDocError('No documents uploaded yet. Please go to Deal Intake & Documents, upload the files, then click Continue.')
      return
    }
    setDocError('')
    setDocModalOpen(false)
    const { _permStep, _permIdx } = stepRef
    _permStep?.run({ update: updateDealState })
    setTimeout(() => runFrom((_permIdx ?? 0) + 1), 300)
  }, [runFrom, updateDealState])

  // Syndication YES — run syndication module then continue
  const handleSyndicateYes = useCallback(async () => {
    setSyndicateModalOpen(false)
    updateDealState({ syndicateDecisionMade: true, syndicateWanted: true })
    const { _permStep, _permIdx } = stepRef
    _permStep?.run({ update: updateDealState })
    await sleep(300)
    runFrom((_permIdx ?? 0) + 1) // goes to run_syndication step
  }, [runFrom, updateDealState])

  // Syndication NO — skip syndication, go directly to loan package
  const handleSyndicateNo = useCallback(async () => {
    setSyndicateModalOpen(false)
    updateDealState({ syndicateDecisionMade: true, syndicateWanted: false, syndicateSkipped: true })
    const { _permIdx } = stepRef
    const pipeline = buildPipeline(useDealStore.getState().dealState)
    const loanPkgIdx = pipeline.findIndex(s => s.id === 'generate_loan_package')
    await sleep(300)
    runFrom(loanPkgIdx >= 0 ? loanPkgIdx : (_permIdx ?? 0) + 2)
  }, [runFrom, updateDealState])

  const handleApprove = useCallback(async () => {
    const { _permStep, _permIdx } = stepRef
    const latestState = useDealStore.getState().dealState

    if (!_permStep) return

    if (_permStep.id === 'select_deal_required') {
      if (!latestState.dealId) return
      setDealModalOpen(false)
      _permStep.run({ update: updateDealState })
      await sleep(300)
      runFrom(_permIdx + 1)
      return
    }

    if (_permStep.id === 'select_lender_required' && latestState.lenderMatches?.topRecommendation) {
      updateDealState({
        lenderMatches: {
          ...latestState.lenderMatches,
          topRecommendation: {
            ...latestState.lenderMatches.topRecommendation,
            userApproved: true,
            approvedAt: latestState.lenderMatches.topRecommendation.approvedAt || new Date().toISOString(),
          },
        },
      })
    }

    setPermModal(null)
    setPermMinimized(false)
    _permStep.run({ update: updateDealState })
    await sleep(300)
    runFrom(_permIdx + 1)
  }, [runFrom, updateDealState])

  const handleSkip = useCallback(async () => {
    const { _permIdx } = stepRef
    setPermModal(null)
    setPermMinimized(false)
    await sleep(300)
    runFrom(_permIdx + 1)
  }, [runFrom])

  const handleDealConfirmed = useCallback(async () => {
    const latestState = useDealStore.getState().dealState
    if (!latestState.dealId) {
      setDealError('No deal selected yet. Please open Deal Lead Generation, select a deal card, and click "Confirm Deal Selection" first.')
      return
    }
    setDealError('')
    setDealModalOpen(false)
    setDealMinimized(false)
    const { _permStep, _permIdx } = stepRef
    _permStep?.run({ update: updateDealState })
    await sleep(300)
    runFrom(_permIdx + 1)
  }, [runFrom, updateDealState])

  const handleOmUploaded = useCallback(() => {
    const latestState = useDealStore.getState().dealState
    if (!latestState.financialDocuments?.omUploaded) {
      setOmError('OM not uploaded yet. Please go to Module 3, upload the Offering Memorandum, then click Continue.')
      return
    }
    setOmError('')
    setOmModalOpen(false)
    setOmMinimized(false)
    clearBlockedState()
    runFrom(blockedAtRef.current)
  }, [runFrom])

  const handlePause = () => {
    runningRef.current = false
    setStatus('paused')
    setControlMenuOpen(false)
  }

  const handleResume = () => {
    setControlMenuOpen(false)
    runFrom(stepRef.current)
  }

  // Force-continue after a permission/approval step without re-checking state guards.
  // Used by the control-menu "Resume — I've Confirmed" button.
  const handleForceResume = useCallback(async () => {
    setControlMenuOpen(false)
    const { _permStep, _permIdx } = stepRef

    // If we're paused mid-permission, run the step's side-effect and advance.
    if (_permStep && _permIdx != null) {
      setPermModal(null)
      setPermMinimized(false)
      try { _permStep.run({ update: updateDealState }) } catch (_) {}
      await sleep(300)
      runFrom(_permIdx + 1)
    } else {
      // Fallback: just resume from wherever the step pointer is.
      runFrom(stepRef.current)
    }
  }, [runFrom, updateDealState])

  const handleReset = useCallback(() => {
    clearBlockedState()
    runningRef.current = false
    stepRef.current = 0
    blockedAtRef.current = 0
    setStepIdx(0)
    setStatus('idle')
    setCurrentStep('')
    setPermModal(null)
    setPermMinimized(false)
    setOmModalOpen(false)
    setOmMinimized(false)
    setOmError('')
    setDealModalOpen(false)
    setDealMinimized(false)
    setDealError('')
    setQdaModalOpen(false)
    setPipelineModalOpen(false)
    setDocModalOpen(false)
    setSyndicateModalOpen(false)
    setDocError('')
    setControlMenuOpen(false)
  }, [])

  const handleExit = useCallback(() => { handleReset() }, [handleReset])

  const handleStartFromBeginning = useCallback(() => {
    // Clear all persisted state and run from step 0
    clearBlockedState()
    runningRef.current = false
    stepRef.current = 0
    blockedAtRef.current = 0
    setStepIdx(0)
    setCurrentStep('')
    setPermModal(null)
    setPermMinimized(false)
    setOmModalOpen(false)
    setOmMinimized(false)
    setOmError('')
    setDealModalOpen(false)
    setDealMinimized(false)
    setDealError('')
    setControlMenuOpen(false)
    forceNavigateToModule(1)
    // Small delay to let state settle before kicking off the run
    setTimeout(() => runFrom(0), 80)
  }, [forceNavigateToModule, runFrom])

  const pipeline = buildPipeline(dealState)
  const progress = Math.round((stepIdx / Math.max(pipeline.length, 1)) * 100)

  return (
    <>
      {/* Floating Action Button */}
      <button
        className={`aq-agent-fab ${status === 'running' ? 'fab-running' : ''} ${status === 'waiting' ? 'fab-waiting' : ''} ${status === 'blocked' ? 'fab-blocked' : ''}`}
        onClick={() => setControlMenuOpen((o) => !o)}
        title="AcquireOS Agent"
      >
        <svg className="fab-svg" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="17" stroke="currentColor" strokeWidth="1.5" />
          <rect x="10" y="12" width="16" height="10" rx="3" fill="currentColor" opacity="0.9"/>
          <circle cx="14" cy="17" r="1.5" fill="white"/>
          <circle cx="22" cy="17" r="1.5" fill="white"/>
          <rect x="15" y="22" width="6" height="2" rx="1" fill="currentColor" opacity="0.7"/>
          <circle cx="10" cy="17" r="1.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
          <circle cx="26" cy="17" r="1.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
        </svg>
        {status === 'running' && <span className="fab-pulse" />}
        {status === 'waiting' && <span className="fab-pulse perm-pulse" />}
        {status === 'waiting' && <span className="fab-badge">!</span>}
        {status === 'blocked' && <span className="fab-pulse perm-pulse" />}
        {status === 'blocked' && <span className="fab-badge">!</span>}
      </button>

      {/* Control Menu */}
      {controlMenuOpen && (
        <div className="aq-control-menu">
          <div className="aq-ctrl-header">
            <div className="aq-ctrl-logo">
              <img src="/assets/LOGO-new.png" alt="AcquireOS AI" />
            </div>
            <div>
              <div className="aq-ctrl-name">AcquireOS Agent</div>
              <div className={`aq-ctrl-status status-${status}`}>
                {status === 'idle'    && 'Ready to start'}
                {status === 'running' && 'Running workflow...'}
                {status === 'waiting' && 'Waiting for your input'}
                {status === 'blocked' && 'Action required'}
                {status === 'paused'  && 'Paused'}
                {status === 'done'    && 'All modules complete'}
              </div>
            </div>
          </div>

          {status !== 'idle' && (
            <div className="aq-ctrl-progress-wrap">
              <div className="aq-ctrl-progress-track">
                <div className="aq-ctrl-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="aq-ctrl-progress-label">Step {stepIdx} / {pipeline.length}</span>
            </div>
          )}

          {currentStep && status !== 'idle' && (
            <div className="aq-ctrl-current-step">{currentStep}</div>
          )}

          <div className="aq-ctrl-buttons">
            {status === 'idle' && (
              <>
                <button className="aq-ctrl-btn primary" onClick={handleStart}>▶ Resume / Start</button>
                <button className="aq-ctrl-btn secondary" onClick={handleStartFromBeginning}>⏮ Start from Beginning</button>
                <button className="aq-ctrl-btn ghost" onClick={() => setControlMenuOpen(false)}>Close</button>
              </>
            )}
            {status === 'running' && (
              <>
                <button className="aq-ctrl-btn warning" onClick={handlePause}>Pause</button>
                <button className="aq-ctrl-btn secondary" onClick={handleReset}>Restart</button>
                <button className="aq-ctrl-btn danger" onClick={handleExit}>Exit Agent</button>
              </>
            )}
            {status === 'paused' && (
              <>
                <button className="aq-ctrl-btn primary" onClick={handleResume}>Resume</button>
                <button className="aq-ctrl-btn secondary" onClick={handleStartFromBeginning}>⏮ Start from Beginning</button>
                <button className="aq-ctrl-btn danger" onClick={handleExit}>Exit Agent</button>
              </>
            )}
            {status === 'blocked' && (
              <>
                <button
                  className="aq-ctrl-btn primary"
                  onClick={() => {
                    setControlMenuOpen(false)
                    clearBlockedState()
                    runFrom(blockedAtRef.current)
                  }}
                >
                  ▶ Resume — OM Uploaded
                </button>
                <button className="aq-ctrl-btn secondary" onClick={() => { setOmModalOpen(true); setControlMenuOpen(false) }}>Upload OM</button>
                <button className="aq-ctrl-btn ghost" onClick={handleReset}>Restart</button>
                <button className="aq-ctrl-btn danger" onClick={handleExit}>Exit Agent</button>
              </>
            )}
            {status === 'waiting' && (
              <>
                <button
                  className="aq-ctrl-btn primary"
                  onClick={handleForceResume}
                >
                  ▶ Resume — I've Confirmed
                </button>
                <button className="aq-ctrl-btn secondary" onClick={handleReset}>Restart</button>
                <button className="aq-ctrl-btn danger" onClick={handleExit}>Exit Agent</button>
              </>
            )}
            {status === 'done' && (
              <>
                <button className="aq-ctrl-btn secondary" onClick={handleStartFromBeginning}>⏮ Start from Beginning</button>
                <button className="aq-ctrl-btn ghost" onClick={handleExit}>Close</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Agent Running Bar */}
      {status === 'running' && (
        <div className="aq-running-bar">
          <div className="aq-running-dot-anim" />
          <div className="aq-running-text">
            <span className="aq-running-title">Agent Running</span>
            {currentStep && <span className="aq-running-step">{currentStep}</span>}
          </div>
          <button className="aq-running-pause-btn" onClick={handlePause} title="Pause">II</button>
        </div>
      )}

      {/* Agent Paused Bar */}
      {status === 'paused' && (
        <div className="aq-running-bar aq-paused-bar">
          <div className="aq-paused-icon">⏸</div>
          <div className="aq-running-text">
            <span className="aq-running-title">Agent Paused</span>
            {currentStep && <span className="aq-running-step">Stopped at: {currentStep}</span>}
          </div>
          <button className="aq-running-pause-btn aq-resume-btn" onClick={handleResume} title="Resume">▶ Resume</button>
        </div>
      )}

      {/* Done Banner */}
      {status === 'done' && (
        <div className="aq-done-bar">
          <span>All 16 acquisition modules complete!</span>
          <button onClick={handleReset}>Dismiss</button>
        </div>
      )}

      {/* QDA OM Upload Modal */}
      {qdaModalOpen && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">📄</div>
            <h2 className="aq-modal-title">Upload OM for Quick Deal Analysis</h2>
            <p className="aq-modal-body">
              Upload your <strong>Offering Memorandum (OM)</strong> in the <strong>Quick Deal Analysis</strong> module.
              The AI will instantly extract key deal metrics — price, NOI, cap rate, and more.
            </p>
            <div className="aq-modal-alert">Upload the OM in Quick Deal Analysis, then click Analyse. Come back here once done.</div>
            <div className="aq-modal-actions">
              <button className="aq-modal-btn secondary" onClick={() => { forceNavigateToModule('quick-deal'); }}>
                Open Quick Deal Analysis
              </button>
              <button className="aq-modal-btn primary" onClick={handleQdaOmUploaded}>
                OM Analysed — Continue
              </button>
              <button className="aq-modal-btn ghost" onClick={() => { setQdaModalOpen(false); handleReset() }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Confirmation Modal */}
      {pipelineModalOpen && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">🚀</div>
            <h2 className="aq-modal-title">Continue into Acquisition Pipeline?</h2>
            <p className="aq-modal-body">
              Quick Deal Analysis is complete! The AI has reviewed the deal metrics.
              Ready to enter the full <strong>Acquisition Pipeline</strong> starting with Deal Lead Generation?
            </p>
            <div className="aq-modal-actions">
              <button className="aq-modal-btn primary" onClick={handlePipelineConfirmed}>
                Yes — Enter Acquisition Pipeline
              </button>
              <button className="aq-modal-btn ghost" onClick={() => { setPipelineModalOpen(false); handleReset() }}>
                Not Yet — Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {docModalOpen && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">📁</div>
            <h2 className="aq-modal-title">Upload Deal Documents</h2>
            <p className="aq-modal-body">
              Upload all deal documents in <strong>Deal Intake &amp; Documents</strong> — rent roll,
              T12 operating statements, inspection reports, and leases. The AI will validate and extract financials from these.
            </p>
            <div className="aq-modal-alert">Upload documents in Module 3, then click Continue here.</div>
            {docError && <div className="aq-modal-error">{docError}</div>}
            <div className="aq-modal-actions">
              <button className="aq-modal-btn secondary" onClick={() => { setDocError(''); forceNavigateToModule(3) }}>
                Open Deal Intake &amp; Documents
              </button>
              <button className="aq-modal-btn primary" onClick={handleDocUploaded}>
                Documents Uploaded — Continue
              </button>
              <button className="aq-modal-btn ghost" onClick={() => { setDocModalOpen(false); handleReset() }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Syndication Decision Modal */}
      {syndicateModalOpen && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">🤝</div>
            <h2 className="aq-modal-title">Deal Syndication Engine</h2>
            <p className="aq-modal-body">
              Do you need to <strong>syndicate this deal</strong> with co-investors to bridge the equity gap?
            </p>
            <div className="aq-perm-detail" style={{ textAlign: 'left', marginBottom: 16 }}>
              <div className="aq-perm-row"><span>💰</span><strong>Equity Gap Coverage</strong></div>
              <div className="aq-perm-row"><span>📊</span><strong>Ownership Allocation per investor</strong></div>
              <div className="aq-perm-row"><span>📈</span><strong>Projected ROI and IRR per ticket</strong></div>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
              ⚡ <strong>Optional</strong> — skip if funding solo or through a single lender.
            </p>
            <div className="aq-modal-actions">
              <button className="aq-modal-btn primary" onClick={handleSyndicateYes}>
                Yes — Enter Syndication Engine
              </button>
              <button className="aq-modal-btn secondary" onClick={handleSyndicateNo}>
                Skip — Go to Loan Package Generator
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Selection Modal */}
      {dealModalOpen && !dealMinimized && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">&#127970;</div>
            <h2 className="aq-modal-title">Select a Deal</h2>
            <p className="aq-modal-body">
              Open <strong>Deal Lead Generation</strong>, select a deal card, and click
              <em> Confirm Deal Selection</em>. Then come back here and press Continue.
            </p>
            {dealError && <div className="aq-modal-error">{dealError}</div>}
            <div className="aq-modal-actions">
              <button
                className="aq-modal-btn secondary"
                onClick={() => { setDealError(''); forceNavigateToModule(1); setDealMinimized(true) }}
              >
                Open Deal Lead Generation
              </button>
              <button className="aq-modal-btn primary" onClick={handleDealConfirmed}>
                I have Selected a Deal - Continue
              </button>
              <button
                className="aq-modal-btn ghost"
                onClick={() => { setDealModalOpen(false); setDealMinimized(false); handleReset() }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Minimized Chip */}
      {dealModalOpen && dealMinimized && (
        <div className="aq-minimized-chip" onClick={() => setDealMinimized(false)}>
          <span className="aq-chip-dot" />
          <span className="aq-chip-text">Select a deal, then tap here to confirm</span>
          <span className="aq-chip-arrow">^</span>
        </div>
      )}

      {/* OM Upload Modal */}
      {omModalOpen && !omMinimized && (
        <div className="aq-overlay">
          <div className="aq-modal">
            <div className="aq-modal-emoji">&#128196;</div>
            <h2 className="aq-modal-title">Upload Offering Memorandum</h2>
            <p className="aq-modal-body">
              The agent needs an <strong>Offering Memorandum (OM)</strong> to extract financials.
              Upload it in <strong>Module 3 - Deal Intake and Documents</strong>, then come back and press Continue.
            </p>
            <div className="aq-modal-alert">
              The agent cannot proceed without an uploaded OM. This step is mandatory.
            </div>
            {omError && <div className="aq-modal-error">{omError}</div>}
            <div className="aq-modal-actions">
              <button
                className="aq-modal-btn secondary"
                onClick={() => { setOmError(''); forceNavigateToModule(3); setOmMinimized(true) }}
              >
                Go to Deal Intake and Documents
              </button>
              <button className="aq-modal-btn primary" onClick={handleOmUploaded}>
                OM Uploaded - Continue
              </button>
              <button
                className="aq-modal-btn ghost"
                onClick={() => { setOmModalOpen(false); setOmMinimized(false); handleReset() }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OM Minimized Chip */}
      {omModalOpen && omMinimized && (
        <div className="aq-minimized-chip aq-chip-om" onClick={() => setOmMinimized(false)}>
          <span className="aq-chip-dot" />
          <span className="aq-chip-text">Upload OM in Module 3, then tap here to continue</span>
          <span className="aq-chip-arrow">^</span>
        </div>
      )}

      {/* Permission / Approval Modal — minimized chip */}
      {permModal && permMinimized && (
        <div
          className="aq-minimized-chip aq-chip-perm"
          onClick={() => {
            if (permModal.step.id === 'select_lender_required' && lenderApprovalReady) {
              handleApprove()
            } else {
              setPermMinimized(false)
            }
          }}
        >
          <span className="aq-chip-dot" />
          <span className="aq-chip-text">
            {permModal.step.id === 'select_lender_required' && lenderApprovalReady
              ? 'Lender confirmed - tap to resume agent'
              : permModal.step.permissionDetail?.moduleNumber
              ? `Complete action in Module ${permModal.step.permissionDetail.moduleNumber}, then tap here to confirm`
              : 'Complete the required action, then tap here to confirm'}
          </span>
          <span className="aq-chip-arrow">^</span>
        </div>
      )}

      {/* Permission / Approval Modal */}
      {permModal && !permMinimized && (
        <div className="aq-overlay">
          <div className="aq-modal aq-perm-modal">
            {(() => {
              const { step } = permModal
              const d = step.permissionDetail || {}
              const isEmail    = step.permission === 'email'
              const isWire     = step.permission === 'wire'
              const isDecision = step.permission === 'decision'
              return (
                <>
                  <div className={`aq-perm-badge ${isEmail ? 'badge-email' : isWire ? 'badge-wire' : 'badge-decision'}`}>
                    {isEmail ? 'Email Approval Required' : isWire ? 'Wire Authorization Required' : 'Your Decision Required'}
                  </div>
                  <h2 className="aq-modal-title">{isDecision ? d.title : step.label}</h2>
                  {isDecision && (
                    <div className="aq-perm-detail">
                      <p className="aq-perm-action">{d.action}</p>
                      <p className="aq-perm-reason">{d.reason}</p>
                    </div>
                  )}
                  {isEmail && (
                    <div className="aq-perm-detail">
                      <div className="aq-perm-row"><span>To</span><strong>{d.to}</strong></div>
                      <div className="aq-perm-row"><span>Subject</span><strong>{d.subject}</strong></div>
                      <div className="aq-perm-preview">{d.preview}</div>
                    </div>
                  )}
                  {isWire && (
                    <div className="aq-perm-detail">
                      <div className="aq-perm-row"><span>Amount</span><strong className="wire-amount">{money(d.amount)}</strong></div>
                      <div className="aq-perm-row"><span>To</span><strong>{d.to}</strong></div>
                      <div className="aq-perm-row"><span>Bank</span><strong>{d.bank}</strong></div>
                      <div className="aq-perm-row"><span>Memo</span><strong>{d.memo}</strong></div>
                      {d.warning && <div className="aq-perm-warning">{d.warning}</div>}
                    </div>
                  )}
                  <div className="aq-modal-actions">
                    {isDecision && d.moduleNumber && (
                      <button
                        className="aq-modal-btn secondary"
                        onClick={() => {
                          forceNavigateToModule(d.moduleNumber)
                          setPermMinimized(true)
                        }}
                      >
                        Open Module {d.moduleNumber}
                      </button>
                    )}
                    <button className="aq-modal-btn primary" onClick={handleApprove}>
                      {isDecision ? 'I Confirmed This' : 'Approve and Continue'}
                    </button>
                    {!isDecision && (
                      <button className="aq-modal-btn ghost" onClick={handleSkip}>Skip</button>
                    )}
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      )}
    </>
  )
}
